// TheHeaderDropdown.jsx
import { useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { startLogout } from "../../redux/actions/administrador/auth/auth";
import { startLogoutTeacher } from "../../redux/actions/administrador/auth/authTeachers";
import { AvatarApp } from "../AvatarApp";
import { IconApp } from "../IconApp";
import { EnterpriseCard } from "../EnterpriseCard";
import usePWAInstall from "@hooks/usePWAInstall";
import { useEnterpriseSelection } from "../../hooks/useEnterpriseSelection";

const TheHeaderDropdown = () => {
    const { user, session } = useSelector((state) => state.auth);
    const {
        enterprises,
        activeEnterprise,
        handleSelectEnterprise,
        hasEnterprises,
    } = useEnterpriseSelection();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const toggleRef = useRef(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { available, installing, isIOS, install } = usePWAInstall({
        onIOSFallback: () => {
            // Aquí abre un modal/alert con pasos para iOS:
            // 1) Abre en Safari
            // 2) Toca el ícono "Compartir"
            // 3) Selecciona "Añadir a pantalla de inicio"
            alert(
                "Para instalar en iPhone/iPad:\n\n1) Abre en Safari\n2) Toca Compartir (cuadro con flecha)\n3) 'Añadir a pantalla de inicio'"
            );
        },
    });

    const handleLogout = async () => {
        let redirect = "/";
        switch (session) {
            case "teacher":
                await dispatch(startLogout());
                redirect = "/teachers/login";
                break;
            case "student":
                await dispatch(startLogout());
                redirect = "/students/login";
                break;
            default:
                await dispatch(startLogout());
                break;
        }
        window.location.replace(redirect);
    };

    const handleMyProfile = () => navigate(`/app/profile`);

    // Limpiar selección si la empresa ya no existe
    useEffect(() => {
        if (enterprises.length === 0 && activeEnterprise) {
            handleSelectEnterprise(null);
        }
    }, [activeEnterprise, enterprises]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                toggleRef.current &&
                !toggleRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const handleToggle = () => {
        setIsOpen((prev) => !prev);
    };

    const runAndClose = (action) => {
        action();
        setIsOpen(false);
    };

    return (
        <div className="dropdown c-header-nav-items mx-2">
            <button
                type="button"
                className="c-header-nav-link"
                aria-expanded={isOpen}
                onClick={handleToggle}
                ref={toggleRef}
            >
                <label
                    className="mx-2 "
                    style={{ marginTop: ".65rem" }}
                    id={user.id}
                >
                    {user.username}
                </label>
                <div className="c-avatar">
                    <AvatarApp
                        img={user.uri}
                        size={35}
                        textToGenerateAvatar={user.email}
                    />
                </div>
            </button>

            <div
                className={`dropdown-menu dropdown-menu-end pt-0 header-user-dropdown-menu${
                    isOpen ? " show" : ""
                }`}
                style={{ minWidth: "230px" }}
                ref={dropdownRef}
            >
                <div
                    className="dropdown-header text-center"
                    style={{
                        fontSize: "11px",
                        backgroundColor: "rgb(235, 237, 239)",
                        color: "#727272ff",
                    }}
                >
                    <strong>Usuario</strong>
                </div>

                {/* Mostrar opción de instalación si está disponible */}
                {available && (
                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => runAndClose(install)}
                        disabled={installing}
                    >
                        <IconApp iconClassName="fas fa-download mfe-2" />
                        {installing
                            ? "Abriendo instalador…"
                            : "Instalar como app"}
                    </button>
                )}

                {/* iOS: si no hay prompt disponible pero quieres guiar al usuario */}
                {!available && isIOS && (
                    <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => runAndClose(install)}
                    >
                        <IconApp iconClassName="fas fa-mobile-alt mfe-2" />
                        Cómo instalar en iPhone/iPad
                    </button>
                )}

                <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => runAndClose(handleMyProfile)}
                >
                    <IconApp iconClassName="fas fa-user-alt mfe-2" />
                    Ver mi perfil
                </button>

                <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => runAndClose(handleLogout)}
                >
                    <IconApp iconClassName="fas fa-sign-out-alt mfe-2" />
                    Cerrar sesión
                </button>

                {/* Empresas (al final) */}
                {hasEnterprises && (
                    <>
                        <div
                            className="dropdown-header text-left py-2"
                            style={{
                                fontSize: "11px",
                                backgroundColor: "rgb(235, 237, 239)",
                                color: "#727272ff",
                            }}
                        >
                            <strong>Empresas</strong>
                        </div>
                        <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                            {enterprises.map((enterprise) => (
                                <button
                                    key={enterprise.id || enterprise.tiny_name}
                                    type="button"
                                    className={`dropdown-item d-flex align-items-center py-2${
                                        activeEnterprise &&
                                        activeEnterprise.id === enterprise.id
                                            ? " active"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        runAndClose(() =>
                                            handleSelectEnterprise(enterprise)
                                        )
                                    }
                                >
                                    <EnterpriseCard
                                        enterprise={enterprise}
                                        active={
                                            activeEnterprise &&
                                            activeEnterprise.id ===
                                                enterprise.id
                                        }
                                    />
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TheHeaderDropdown;
